import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'
import { KnowledgeBaseFileUpload } from '@/components/KnowledgeBaseFileUpload'
import { LeadCaptureTab } from '@/components/LeadCaptureTab'
import { usePlan } from '@/hooks/usePlan'
import { Modal, ModalType } from '@/components/Modal'
import { WidgetPreview } from '@/components/WidgetPreview'
import { WidgetIcon } from '@/components/WidgetIcon'

const WIDGET_ICON_STYLES = [
  { value: 'brand', labelKey: 'widget.iconStyleBrand' },
  { value: 'neutral', labelKey: 'widget.iconStyleNeutral' },
  { value: 'dots', labelKey: 'widget.iconStyleDots' },
  { value: 'rounded', labelKey: 'widget.iconStyleRounded' },
  { value: 'spark', labelKey: 'widget.iconStyleSpark' },
]

interface ModalState {
  isOpen: boolean
  title: string
  message: string
  type: ModalType
  onConfirm?: () => void
  confirmText?: string
}

// @ts-ignore
const WIDGET_VERSION = typeof __WIDGET_VERSION__ !== 'undefined' ? __WIDGET_VERSION__ : 'latest'

interface AIConfig {
  usePlatformKey: boolean
  customApiKey?: string
  temperature: number
  maxTokens: number
  tone: 'professional' | 'casual' | 'friendly' | 'technical'
  language: string
  knowledgeBase: string[]
  knowledgeBaseFileName?: string
  companyDescription: string
  servicesDescription: string
  enableCustomPrompt: boolean
  enableKnowledgeBase: boolean
  systemPrompt: string
}

interface QuickAction {
  id: string
  label: string
  action_type: string
  action_value: string
  sort_order: number
  is_active: boolean
}

interface WidgetConfig {
  tenantId?: string
  position: string
  theme: string
  primaryColor: string
  botName: string
  greeting: string
  placeholder: string
  showPopup: boolean
  popupDelay: number
  soundEnabled: boolean
  showFormSuggestion?: boolean
  avatarUrl?: string
  customCss?: string
  showPoweredBy?: boolean
  iconStyle?: string
  language: string
  tone: string
  aiConfig?: AIConfig
}

export const Widget: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { tenant } = useAuthStore()
  const { plan, hasFeature, getPlanLimit } = usePlan()
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<'appearance' | 'ai' | 'quickActions' | 'leadCapture'>('appearance')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // True when the loaded AI config uses a custom key (the key itself is never returned by the API)
  const [hasSavedApiKey, setHasSavedApiKey] = useState(false)
  const [newKnowledgeItem, setNewKnowledgeItem] = useState('')
  const [newQuickAction, setNewQuickAction] = useState({ label: '', actionType: 'message', actionValue: '' })
  const [editingQuickAction, setEditingQuickAction] = useState<string | null>(null)
  const [editQuickActionData, setEditQuickActionData] = useState({ label: '', actionType: 'message', actionValue: '' })

  // Plan-based feature flags
  const canUseCustomPrompt = hasFeature('custom_ai')
  const canUseKnowledgeBaseFile = hasFeature('knowledge_base_file')
  const canUseKnowledgeBase = hasFeature('knowledge_base_manual')
  const canUseBYOK = hasFeature('byok')
  const canUseWhiteLabel = hasFeature('white_label')
  const canUseNeutralIcon = hasFeature('neutral_icon')
  const canUseAvatar = hasFeature('widget_avatar')
  const canUseCustomCss = hasFeature('custom_css')
  const maxKnowledgeBaseEntries = getPlanLimit('maxKnowledgeBaseEntries')

  const tenantId = tenant?.id

  const { data: config, isLoading } = useQuery({
    queryKey: ['widget-config', tenantId],
    queryFn: async () => {
      const response = await api.get('/widgets/config')
      return response.data.data as WidgetConfig
    },
  })

  const { data: quickActions, isLoading: quickActionsLoading } = useQuery({
    queryKey: ['quick-actions', tenantId],
    queryFn: async () => {
      const response = await api.get('/quick-actions')
      return response.data.data as QuickAction[]
    },
  })

  const updateWidgetMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put('/widgets/config', data)
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-config', tenantId] })
      showSuccess()
    },
    onError: (error: any) => {
      const base = error?.response?.data?.error || t('widget.saveConfigError') || 'Failed to save widget configuration'
      const details = error?.response?.data?.details
      const detailText = Array.isArray(details) && details.length > 0
        ? ': ' + details.map((d: any) => d.message || JSON.stringify(d)).join(', ')
        : ''
      setSaveError(base + detailText)
    },
  })

  const updateAIMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put('/widgets/ai-config', data)
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-config', tenantId] })
      showSuccess()
    },
    onError: (error: any) => {
      const base = error?.response?.data?.error || t('widget.saveAIConfigError') || 'Failed to save AI configuration'
      const details = error?.response?.data?.details
      const detailText = Array.isArray(details) && details.length > 0
        ? ': ' + details.map((d: any) => d.message || JSON.stringify(d)).join(', ')
        : ''
      setSaveError(base + detailText)
    },
  })

  const createQuickActionMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/quick-actions', data)
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] })
      setNewQuickAction({ label: '', actionType: 'message', actionValue: '' })
      showSuccess()
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.error || t('widget.saveQuickActionError') || 'Failed to save quick action')
    },
  })

  const updateQuickActionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/quick-actions/${id}`, data)
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] })
      setEditingQuickAction(null)
      showSuccess()
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.error || t('widget.saveQuickActionError') || 'Failed to save quick action')
    },
  })

  const deleteQuickActionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/quick-actions/${id}`)
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] })
      closeModal()
      showSuccess()
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.error || t('widget.deleteQuickActionError') || 'Failed to delete quick action')
    },
  })

  const reorderQuickActionsMutation = useMutation({
    mutationFn: async (orders: { id: string; sortOrder: number }[]) => {
      const response = await api.post('/quick-actions/reorder', { orders })
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] })
    },
    onError: (error: any) => {
      setSaveError(error?.response?.data?.error || t('widget.reorderQuickActionError') || 'Failed to reorder quick actions')
    },
  })

  const showSuccess = () => {
    setSaveSuccess(true)
    setSaveError(null)
    setTimeout(() => setSaveSuccess(false), 3000)
  }

  // Widget appearance form state
  const [formData, setFormData] = useState({
    position: 'bottom-right',
    theme: 'dark',
    primaryColor: '#006cd4',
    botName: '',
    greeting: '',
    placeholder: 'Type your message...',
    showPopup: true,
    popupDelay: 5, // seconds in the UI — converted to ms on save
    soundEnabled: false,
    showFormSuggestion: true,
    avatarUrl: '',
    customCss: '',
    showPoweredBy: true,
    iconStyle: 'brand',
    language: 'en',
    tone: 'professional',
    contactEmail: '',
    contactPhone: '',
  })

  // AI config form state
  const [aiFormData, setAIFormData] = useState<AIConfig>({
    usePlatformKey: true,
    customApiKey: '',
    temperature: 0.7,
    maxTokens: 1024,
    tone: 'professional',
    language: 'en',
    knowledgeBase: [],
    knowledgeBaseFileName: '',
    companyDescription: '',
    servicesDescription: '',
    enableCustomPrompt: false,
    enableKnowledgeBase: false,
    systemPrompt: '',
  })

  React.useEffect(() => {
    if (config) {
      setFormData((prev) => ({
        ...prev,
        ...config,
        avatarUrl: config.avatarUrl || '',
        customCss: config.customCss || '',
        showPoweredBy: config.showPoweredBy !== false,
        showFormSuggestion: config.showFormSuggestion !== false,
        iconStyle: config.iconStyle || 'brand',
        // popupDelay is stored in ms, shown in seconds
        popupDelay: config.popupDelay ? Math.round(config.popupDelay / 1000) : prev.popupDelay,
      }))
      if (config.aiConfig) {
        const aiConfig = config.aiConfig
        setAIFormData((prev) => ({ ...prev, ...aiConfig, language: config.language || aiConfig.language || prev.language }))
        setHasSavedApiKey(aiConfig.usePlatformKey === false)
      }
    }
  }, [config])

  const handleWidgetSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateWidgetMutation.mutate({
      position: formData.position,
      theme: formData.theme,
      primaryColor: formData.primaryColor,
      botName: formData.botName,
      greeting: formData.greeting,
      placeholder: formData.placeholder,
      showPopup: formData.showPopup,
      // UI works in seconds, API expects ms (1000–60000)
      popupDelay: Math.min(60, Math.max(1, formData.popupDelay || 5)) * 1000,
      soundEnabled: formData.soundEnabled,
      showFormSuggestion: formData.showFormSuggestion,
      avatarUrl: formData.avatarUrl,
      customCss: formData.customCss,
      showPoweredBy: formData.showPoweredBy,
      iconStyle: formData.iconStyle,
      language: formData.language,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
    })
  }

  const handleAISubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: Record<string, any> = {
      usePlatformKey: aiFormData.usePlatformKey,
      tone: aiFormData.tone,
      language: formData.language,
      companyDescription: aiFormData.companyDescription,
      servicesDescription: aiFormData.servicesDescription,
      systemPrompt: aiFormData.systemPrompt,
      knowledgeBase: aiFormData.knowledgeBase,
    }
    // Only send a custom key when the user typed a new one — an empty
    // string would wipe the key already stored server-side
    if (!aiFormData.usePlatformKey && aiFormData.customApiKey?.trim()) {
      payload.customApiKey = aiFormData.customApiKey.trim()
    }
    updateAIMutation.mutate(payload)
  }

  const addKnowledgeItem = () => {
    if (newKnowledgeItem.trim()) {
      setAIFormData((prev) => ({
        ...prev,
        knowledgeBase: [...prev.knowledgeBase, newKnowledgeItem.trim()],
      }))
      setNewKnowledgeItem('')
    }
  }

  const removeKnowledgeItem = (index: number) => {
    setAIFormData((prev) => ({
      ...prev,
      knowledgeBase: prev.knowledgeBase.filter((_, i) => i !== index),
    }))
  }

  const handleCreateQuickAction = (e: React.FormEvent) => {
    e.preventDefault()
    if (newQuickAction.label.trim() && newQuickAction.actionValue.trim()) {
      createQuickActionMutation.mutate({
        label: newQuickAction.label.trim(),
        actionType: newQuickAction.actionType,
        actionValue: newQuickAction.actionValue.trim(),
      })
    }
  }

  const handleUpdateQuickAction = (id: string) => {
    updateQuickActionMutation.mutate({
      id,
      data: {
        label: editQuickActionData.label,
        actionType: editQuickActionData.actionType,
        actionValue: editQuickActionData.actionValue,
      },
    })
  }

  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  })

  const showModal = (title: string, message: string, type: ModalType = 'info', onConfirm?: () => void, confirmText?: string) => {
    setModal({ isOpen: true, title, message, type, onConfirm, confirmText })
  }

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  const handleDeleteQuickAction = (id: string) => {
    showModal(
      t('widget.deleteTitle') || 'Delete Quick Action',
      t('widget.deleteConfirm'),
      'confirm',
      () => deleteQuickActionMutation.mutate(id),
      t('common.delete')
    )
  }

  const moveQuickAction = (index: number, direction: 'up' | 'down') => {
    if (!quickActions) return
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= quickActions.length) return

    const reordered = [...quickActions]
    const temp = reordered[index]
    reordered[index] = reordered[newIndex]
    reordered[newIndex] = temp

    const orders = reordered.map((qa, i) => ({ id: qa.id, sortOrder: i + 1 }))
    reorderQuickActionsMutation.mutate(orders)
  }

  const startEditQuickAction = (qa: QuickAction) => {
    setEditingQuickAction(qa.id)
    setEditQuickActionData({
      label: qa.label,
      actionType: qa.action_type,
      actionValue: qa.action_value,
    })
  }

  const embedCode = `<script>
  (function(w,d,s,o,f,js,fjs){
    w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(Array.prototype.slice.call(arguments))};
    js=d.createElement(s),fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','r3Converse','https://cdn.r3converse.com/widget.js?v=` + __WIDGET_VERSION__ + `'));

  // Retry mechanism: widget may take time to load
  function tryInit(attempts) {
    if (typeof r3Converse === 'function') {
      r3Converse('init', {
        tenantId: '${config?.tenantId || 'your-tenant-id'}',
        position: '${config?.position || 'bottom-right'}',
        theme: '${config?.theme || 'dark'}'
      });
    } else if (attempts > 0) {
      setTimeout(function() { tryInit(attempts - 1); }, 500);
    } else {
      console.error('r3Converse widget failed to load after 10 attempts');
    }
  }
  tryInit(10);
</script>`

  const copyCode = () => {
    navigator.clipboard.writeText(embedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (isLoading) return <div className="text-gray-400">{t('common.loading')}</div>

  const knowledgeBaseEntryCount = aiFormData.knowledgeBase.length
  const isAtKnowledgeBaseLimit = maxKnowledgeBaseEntries !== -1 && knowledgeBaseEntryCount >= maxKnowledgeBaseEntries

  const getActionTypeIcon = (type: string) => {
    switch (type) {
      case 'link': return '🔗'
      case 'form': return '📋'
      default: return '💬'
    }
  }

  const getActionTypeLabel = (type: string) => {
    switch (type) {
      case 'link': return t('widget.actionOpenLink')
      case 'form': return t('widget.actionShowForm')
      default: return t('widget.actionSendMessage')
    }
  }

  return (
    <div>
      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={t('common.cancel')}
        isLoading={deleteQuickActionMutation.isPending}
      />

      <h1 className="text-3xl font-bold mb-8">{t('widget.title')}</h1>

      {/* Success Toast */}
      {saveSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          {t('widget.saved')}
        </div>
      )}

      {saveError && (
        <div className="fixed top-16 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          {saveError}
        </div>
      )}

      {/* Tabs - Desktop: horizontal buttons, Mobile: native select */}
      <div className="mb-8">
        {/* Desktop tabs */}
        <div className="hidden sm:flex gap-1 bg-[#1a1a25] p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('appearance')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'appearance'
                ? 'bg-[#006cd4] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t('widget.appearance')}
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'ai'
                ? 'bg-[#006cd4] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t('widget.aiConfig')}
          </button>
          <button
            onClick={() => setActiveTab('quickActions')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'quickActions'
                ? 'bg-[#006cd4] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t('widget.quickActions')}
          </button>
          <button
            onClick={() => setActiveTab('leadCapture')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'leadCapture'
                ? 'bg-[#006cd4] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t('widget.leadCapture') || 'Lead Capture'}
          </button>
        </div>

        {/* Mobile select */}
        <div className="sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as any)}
            className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-medium focus:outline-none focus:border-[#006cd4]"
          >
            <option value="appearance">{t('widget.appearance')}</option>
            <option value="ai">{t('widget.aiConfig')}</option>
            <option value="quickActions">{t('widget.quickActions')}</option>
            <option value="leadCapture">{t('widget.leadCapture') || 'Lead Capture'}</option>
          </select>
        </div>
      </div>

      {activeTab === 'appearance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Appearance Form */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-6">{t('widget.appearance')}</h2>

            <form onSubmit={handleWidgetSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.position')}</label>
                <select
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                >
                  <option value="bottom-right">{t('widget.bottomRight')}</option>
                  <option value="bottom-left">{t('widget.bottomLeft')}</option>
                  <option value="top-right">{t('widget.topRight')}</option>
                  <option value="top-left">{t('widget.topLeft')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.theme')}</label>
                <select
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                >
                  <option value="dark">{t('widget.dark')}</option>
                  <option value="light">{t('widget.light')}</option>
                  <option value="auto">{t('widget.auto')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.language')}</label>
                <select
                  value={formData.language}
                  onChange={(e) => {
                    const language = e.target.value
                    setFormData({ ...formData, language })
                    setAIFormData((prev) => ({ ...prev, language }))
                  }}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                >
                  <option value="en">{t('widget.english')}</option>
                  <option value="es">{t('widget.spanish')}</option>
                  <option value="fr">{t('widget.french')}</option>
                  <option value="de">{t('widget.german')}</option>
                  <option value="pt">{t('widget.portuguese')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">{t('widget.languageHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.primaryColor')}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="w-12 h-12 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="flex-1 px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.botName')}</label>
                <input
                  type="text"
                  value={formData.botName}
                  onChange={(e) => setFormData({ ...formData, botName: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  placeholder={t('widget.botNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.greetingMessage')}</label>
                <textarea
                  value={formData.greeting}
                  onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white h-24 resize-none"
                  placeholder={t('widget.greetingPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.placeholderText')}</label>
                <input
                  type="text"
                  value={formData.placeholder}
                  onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  placeholder={t('widget.placeholderPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.contactEmail')}</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  placeholder="contact@yourcompany.com"
                />
                <p className="text-xs text-gray-500 mt-1">{t('widget.contactEmailHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.contactPhone')}</label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  placeholder="+34 600 000 000"
                />
                <p className="text-xs text-gray-500 mt-1">{t('widget.contactPhoneHint')}</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showPopup"
                  checked={formData.showPopup}
                  onChange={(e) => setFormData({ ...formData, showPopup: e.target.checked })}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="showPopup" className="text-sm text-gray-300">{t('widget.showAutoPopup')}</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.popupDelay')}</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={formData.popupDelay}
                  disabled={!formData.showPopup}
                  onChange={(e) => setFormData({ ...formData, popupDelay: parseInt(e.target.value) || 5 })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white disabled:opacity-50"
                />
                <p className="text-xs text-gray-500 mt-1">{t('widget.popupDelayHint')}</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="soundEnabled"
                  checked={formData.soundEnabled}
                  onChange={(e) => setFormData({ ...formData, soundEnabled: e.target.checked })}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="soundEnabled" className="text-sm text-gray-300">{t('widget.soundEnabled')}</label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showFormSuggestion"
                  checked={formData.showFormSuggestion}
                  onChange={(e) => setFormData({ ...formData, showFormSuggestion: e.target.checked })}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="showFormSuggestion" className="text-sm text-gray-300">
                  {t('widget.showFormSuggestion')}
                </label>
              </div>
              <p className="text-xs text-gray-500 -mt-2">{t('widget.showFormSuggestionHint')}</p>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('widget.avatarUrl')}
                  <span className="text-gray-500 text-xs ml-2">{t('widget.avatarUrlHint')}</span>
                  {!canUseAvatar && (
                    <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {t('widget.proOnly')}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={formData.avatarUrl}
                  disabled={!canUseAvatar}
                  onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white disabled:opacity-50"
                  placeholder="https://example.com/avatar.png"
                />
                <p className="text-xs text-gray-500 mt-2 leading-relaxed bg-[#1a1a25] border border-white/5 rounded-lg p-3">
                  {t('widget.avatarGuidelines')}
                </p>
                {!canUseAvatar && (
                  <p className="text-xs text-gray-500 mt-1">{t('widget.avatarUrlProNote')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('widget.iconStyle')}
                  {!canUseNeutralIcon && (
                    <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {t('widget.growthOnly')}
                    </span>
                  )}
                </label>
                <div className="flex flex-wrap gap-3">
                  {WIDGET_ICON_STYLES.map((opt) => {
                    const selected = formData.iconStyle === opt.value
                    const disabled = !canUseNeutralIcon && opt.value !== 'brand'
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => setFormData({ ...formData, iconStyle: opt.value })}
                        title={t(opt.labelKey)}
                        aria-label={t(opt.labelKey)}
                        aria-pressed={selected}
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white transition-colors ${
                          selected
                            ? 'bg-[#006cd4]/25 border-2 border-[#006cd4] ring-2 ring-[#006cd4]/40'
                            : 'bg-[#1a1a25] border-2 border-white/10 hover:border-white/30'
                        } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <WidgetIcon iconStyle={opt.value} style={{ width: 24, height: 24 }} />
                      </button>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('widget.iconStyleHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('widget.customCss')}
                  <span className="text-gray-500 text-xs ml-2">{t('widget.customCssHint')}</span>
                  {!canUseCustomCss && (
                    <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {t('widget.proOnly')}
                    </span>
                  )}
                </label>
                <textarea
                  value={formData.customCss}
                  disabled={!canUseCustomCss}
                  onChange={(e) => setFormData({ ...formData, customCss: e.target.value })}
                  className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white h-32 resize-none font-mono text-sm disabled:opacity-50"
                  maxLength={10000}
                />
                {!canUseCustomCss && (
                  <p className="text-xs text-gray-500 mt-1">{t('widget.customCssProNote')}</p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="showPoweredBy"
                  checked={formData.showPoweredBy}
                  disabled={!canUseWhiteLabel}
                  onChange={(e) => setFormData({ ...formData, showPoweredBy: e.target.checked })}
                  className="w-5 h-5 rounded disabled:opacity-50"
                />
                <label htmlFor="showPoweredBy" className="text-sm text-gray-300">
                  {t('widget.showPoweredBy')}
                  {!canUseWhiteLabel && (
                    <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {t('widget.enterpriseOnly')}
                    </span>
                  )}
                </label>
              </div>
              {!canUseWhiteLabel && (
                <p className="text-xs text-gray-500 -mt-2">{t('widget.showPoweredByEnterpriseNote')}</p>
              )}

              <button
                type="submit"
                disabled={updateWidgetMutation.isPending}
                className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
              >
                {updateWidgetMutation.isPending ? t('widget.savingAppearance') : t('widget.saveAppearance')}
              </button>
            </form>
          </div>

          {/* Embed Code & Preview */}
          <div className="space-y-6">
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">{t('widget.embedCode')}</h2>
              <p className="text-sm text-gray-400 mb-4">
                {t('widget.embedCodeDesc')}
              </p>
              <div className="relative">
                <pre className="bg-[#1a1a25] border border-white/10 rounded-lg p-4 overflow-x-auto text-sm text-gray-300">
                  <code>{embedCode}</code>
                </pre>
                <button
                  onClick={copyCode}
                  className="absolute top-2 right-2 px-3 py-1 bg-[#006cd4]/20 text-[#006cd4] rounded text-sm hover:bg-[#006cd4]/30 transition-colors"
                >
                  {copied ? t('common.copied') : t('common.copy')}
                </button>
              </div>
            </div>

            <WidgetPreview config={formData} title={t('widget.preview')} />
          </div>
        </div>
      )}

      {activeTab === 'ai' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* AI Provider & Company Context */}
          <div className="space-y-6">
            {/* AI Provider */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('widget.aiProvider')}</h2>
                {!canUseBYOK && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                    {t('widget.enterpriseOnly') || 'Enterprise'}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">
                {t('widget.aiProviderDesc')}
              </p>

              {canUseBYOK ? (
              <form onSubmit={handleAISubmit} className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="usePlatformKey"
                    checked={aiFormData.usePlatformKey}
                    onChange={(e) => setAIFormData({ ...aiFormData, usePlatformKey: e.target.checked })}
                    className="w-5 h-5 rounded"
                  />
                  <label htmlFor="usePlatformKey" className="text-sm text-gray-300">{t('widget.usePlatformKey')}</label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.customApiKey')}</label>
                  <input
                    type="password"
                    value={aiFormData.customApiKey || ''}
                    disabled={aiFormData.usePlatformKey || !canUseBYOK}
                    onChange={(e) => setAIFormData({ ...aiFormData, customApiKey: e.target.value })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white disabled:opacity-50"
                    placeholder={hasSavedApiKey && !aiFormData.customApiKey
                      ? t('widget.customApiKeySaved')
                      : t('widget.customApiKeyPlaceholder')}
                  />
                  {!canUseBYOK && (
                    <p className="text-xs text-gray-500 mt-1">{t('widget.byokEnterpriseNote')}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={updateAIMutation.isPending}
                  className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                >
                  {updateAIMutation.isPending ? t('common.saving') : t('widget.saveProvider')}
                </button>
              </form>
              ) : (
                <div className="bg-[#1a1a25] border border-white/5 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-2">{t('widget.aiProviderEnterprise')}</p>
                  <a href="/billing" className="text-[#006cd4] text-sm hover:underline">
                    {t('common.upgradeToEnterprise')}
                  </a>
                </div>
              )}
            </div>

            {/* Company Context */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">{t('widget.companyContext')}</h2>
              <p className="text-sm text-gray-400 mb-4">
                {t('widget.companyContextDesc')}
              </p>

              <form onSubmit={handleAISubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('widget.companyDescription')}
                    <span className="text-gray-500 text-xs ml-2">{t('widget.companyDescriptionHint')}</span>
                  </label>
                  <textarea
                    value={aiFormData.companyDescription}
                    onChange={(e) => setAIFormData({ ...aiFormData, companyDescription: e.target.value })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white h-24 resize-none"
                    placeholder={t('widget.companyDescriptionPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('widget.servicesProducts')}
                    <span className="text-gray-500 text-xs ml-2">{t('widget.servicesHint')}</span>
                  </label>
                  <textarea
                    value={aiFormData.servicesDescription}
                    onChange={(e) => setAIFormData({ ...aiFormData, servicesDescription: e.target.value })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white h-32 resize-none"
                    placeholder={t('widget.servicesPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.toneOfVoice')}</label>
                  <select
                    value={aiFormData.tone}
                    onChange={(e) => setAIFormData({ ...aiFormData, tone: e.target.value as any })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  >
                    <option value="professional">{t('widget.toneProfessional')}</option>
                    <option value="friendly">{t('widget.toneFriendly')}</option>
                    <option value="casual">{t('widget.toneCasual')}</option>
                    <option value="technical">{t('widget.toneTechnical')}</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={updateAIMutation.isPending}
                  className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                >
                  {updateAIMutation.isPending ? t('widget.savingAIConfig') : t('widget.saveAIConfig')}
                </button>
              </form>
            </div>

            {/* Knowledge Base */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('widget.knowledgeBase')}</h2>
                {canUseKnowledgeBaseFile && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                    Pro
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">
                {t('widget.knowledgeBaseDesc')}
              </p>

              {canUseKnowledgeBase ? (
                <>
                  {/* File Upload */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-300 mb-3">
                      {t('widget.knowledgeBaseFile') || 'Knowledge Base File'}
                    </h3>
                    {canUseKnowledgeBaseFile ? (
                      <>
                        <p className="text-xs text-gray-500 mb-3">
                          {t('widget.knowledgeBaseFileDesc') || 'Upload a .md file with detailed product information. This will be combined with your manual entries.'}
                        </p>
                        <KnowledgeBaseFileUpload
                          fileName={aiFormData.knowledgeBaseFileName}
                          plan={plan}
                          onUploadSuccess={(fileName) => {
                            setAIFormData((prev) => ({ ...prev, knowledgeBaseFileName: fileName }))
                            queryClient.invalidateQueries({ queryKey: ['widget-config', tenantId] })
                            showSuccess()
                          }}
                          onRemoveSuccess={() => {
                            setAIFormData((prev) => ({ ...prev, knowledgeBaseFileName: '' }))
                            queryClient.invalidateQueries({ queryKey: ['widget-config', tenantId] })
                            showSuccess()
                          }}
                        />
                      </>
                    ) : (
                      <div className="bg-[#1a1a25] border border-white/5 rounded-lg p-4 text-center">
                        <p className="text-gray-400 text-sm mb-2">
                          {t('widget.knowledgeBaseFilePro') || 'File upload is available on Pro and higher plans.'}
                        </p>
                        <a href="/billing" className="text-[#006cd4] text-sm hover:underline">
                          {t('common.upgradeToPro')}
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-white/10 pt-4 mb-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-3">
                      {t('widget.manualEntries') || 'Manual Entries'}
                    </h3>
                    {maxKnowledgeBaseEntries !== -1 && (
                      <p className="text-xs text-gray-500 mb-2">
                        {knowledgeBaseEntryCount}/{maxKnowledgeBaseEntries} {t('common.entries')}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    {aiFormData.knowledgeBase.map((item, index) => (
                      <div key={index} className="flex items-start gap-2 bg-[#1a1a25] p-3 rounded-lg">
                        <span className="text-gray-400 text-sm flex-1">{item}</span>
                        <button
                          onClick={() => removeKnowledgeItem(index)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          {t('widget.remove')}
                        </button>
                      </div>
                    ))}
                    {aiFormData.knowledgeBase.length === 0 && (
                      <p className="text-gray-500 text-sm italic">{t('widget.noKnowledgeBase')}</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newKnowledgeItem}
                      onChange={(e) => setNewKnowledgeItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addKnowledgeItem()}
                      disabled={isAtKnowledgeBaseLimit}
                      className="flex-1 px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white text-sm disabled:opacity-50"
                      placeholder={isAtKnowledgeBaseLimit 
                        ? t('widget.knowledgeBaseLimitReached') || 'Entry limit reached' 
                        : t('widget.knowledgeBasePlaceholder')}
                    />
                    <button
                      onClick={addKnowledgeItem}
                      disabled={isAtKnowledgeBaseLimit}
                      className="px-4 py-2 bg-[#006cd4] text-white rounded-lg text-sm hover:bg-[#0052a3] disabled:opacity-50"
                    >
                      {t('widget.add')}
                    </button>
                  </div>

                  <form onSubmit={handleAISubmit} className="mt-4">
                    <button
                      type="submit"
                      disabled={updateAIMutation.isPending}
                      className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                    >
                      {updateAIMutation.isPending ? t('common.saving') : t('widget.saveKnowledgeBase')}
                    </button>
                  </form>
                </>
              ) : (
                <div className="bg-[#1a1a25] border border-white/5 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-2">
                    {t('widget.knowledgeBasePro')}
                  </p>
                  <a href="/billing" className="text-[#006cd4] text-sm hover:underline">
                    {t('common.upgradeToPro')}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Custom System Prompt */}
          <div className="space-y-6">
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('widget.customSystemPrompt')}</h2>
                {!canUseCustomPrompt && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                    {t('common.proFeature')}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">
                {t('widget.customSystemPromptDesc')}
              </p>

              {canUseCustomPrompt ? (
                <form onSubmit={handleAISubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('widget.systemPrompt')}
                      <span className="text-gray-500 text-xs ml-2">{t('widget.maxChars')}</span>
                    </label>
                    <textarea
                      value={aiFormData.systemPrompt}
                      onChange={(e) => setAIFormData({ ...aiFormData, systemPrompt: e.target.value })}
                      className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white h-64 resize-none font-mono text-sm"
                      placeholder={t('widget.systemPromptPlaceholder')}
                      maxLength={5000}
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {aiFormData.systemPrompt.length}/5000
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={updateAIMutation.isPending}
                    className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                  >
                    {updateAIMutation.isPending ? t('common.saving') : t('widget.saveSystemPrompt')}
                  </button>
                </form>
              ) : (
                <div className="bg-[#1a1a25] border border-white/5 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-2">
                    {t('widget.customPromptPro')}
                  </p>
                  <a href="/billing" className="text-[#006cd4] text-sm hover:underline">
                    {t('common.upgradeToPro')}
                  </a>
                </div>
              )}
            </div>

            {/* AI Preview */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">{t('widget.aiPreview')}</h2>
              <div className="bg-[#1a1a25] rounded-lg p-4 space-y-3">
                <div className="text-sm text-gray-400">
                  <span className="font-medium text-white">{t('widget.tone')}</span>{' '}
                  {aiFormData.tone}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="font-medium text-white">{t('widget.language')}</span>{' '}
                  {aiFormData.language}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="font-medium text-white">{t('widget.knowledgeBaseActive')}</span>{' '}
                  {aiFormData.knowledgeBase.length} {t('common.entries')}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="font-medium text-white">{t('widget.customPromptActive')}</span>{' '}
                  {aiFormData.systemPrompt ? t('widget.active') : t('widget.default')}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="font-medium text-white">{t('widget.companyContextActive')}</span>{' '}
                  {aiFormData.companyDescription ? t('widget.set') : t('common.notSet')}
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-3">{t('widget.tipsTitle')}</h2>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tip1')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tip2')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tip3')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tip4')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'quickActions' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Quick Actions List */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4">{t('widget.yourQuickActions')}</h2>
            <p className="text-sm text-gray-400 mb-4">
              {t('widget.quickActionsDesc')}
            </p>

            {quickActionsLoading ? (
              <div className="text-gray-400">{t('common.loading')}</div>
            ) : (
              <div className="space-y-3">
                {quickActions && quickActions.length > 0 ? (
                  quickActions.map((qa, index) => (
                    <div key={qa.id} className="bg-[#1a1a25] border border-white/5 rounded-lg p-4">
                      {editingQuickAction === qa.id ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editQuickActionData.label}
                            onChange={(e) => setEditQuickActionData({ ...editQuickActionData, label: e.target.value })}
                            className="w-full px-3 py-2 bg-[#12121a] border border-white/10 rounded text-white text-sm"
                            placeholder={t('widget.buttonLabelPlaceholder')}
                          />
                          <select
                            value={editQuickActionData.actionType}
                            onChange={(e) => setEditQuickActionData({ ...editQuickActionData, actionType: e.target.value })}
                            className="w-full px-3 py-2 bg-[#12121a] border border-white/10 rounded text-white text-sm"
                          >
                            <option value="message">{t('widget.actionSendMessage')}</option>
                            <option value="link">{t('widget.actionOpenLink')}</option>
                            <option value="form">{t('widget.actionShowForm')}</option>
                          </select>
                          <input
                            type="text"
                            value={editQuickActionData.actionValue}
                            onChange={(e) => setEditQuickActionData({ ...editQuickActionData, actionValue: e.target.value })}
                            className="w-full px-3 py-2 bg-[#12121a] border border-white/10 rounded text-white text-sm"
                            placeholder={editQuickActionData.actionType === 'link' ? t('widget.urlPlaceholder') : t('widget.messageTextPlaceholder')}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdateQuickAction(qa.id)}
                              className="px-4 py-2 bg-[#006cd4] text-white rounded text-sm hover:bg-[#0052a3]"
                            >
                              {t('common.save')}
                            </button>
                            <button
                              onClick={() => setEditingQuickAction(null)}
                              className="px-4 py-2 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{getActionTypeIcon(qa.action_type)}</span>
                            <div>
                              <div className="font-medium text-white">{qa.label}</div>
                              <div className="text-xs text-gray-500">
                                {getActionTypeLabel(qa.action_type)} • {qa.action_value}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => moveQuickAction(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-500 hover:text-white disabled:opacity-30"
                              title={t('widget.moveUp')}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveQuickAction(index, 'down')}
                              disabled={index === quickActions.length - 1}
                              className="p-1 text-gray-500 hover:text-white disabled:opacity-30"
                              title={t('widget.moveDown')}
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => startEditQuickAction(qa)}
                              className="p-1 text-[#006cd4] hover:text-[#0052a3]"
                              title={t('common.edit')}
                            >
                              ✎
                            </button>
                            <button
                              onClick={() => handleDeleteQuickAction(qa.id)}
                              className="p-1 text-red-400 hover:text-red-300"
                              title={t('common.delete')}
                            >
                              🗑
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t('widget.noQuickActions')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Create Quick Action */}
          <div className="space-y-6">
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">{t('widget.addQuickAction')}</h2>
              <form onSubmit={handleCreateQuickAction} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.buttonLabel')}</label>
                  <input
                    type="text"
                    value={newQuickAction.label}
                    onChange={(e) => setNewQuickAction({ ...newQuickAction, label: e.target.value })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                    placeholder={t('widget.buttonLabelPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('widget.actionType')}</label>
                  <select
                    value={newQuickAction.actionType}
                    onChange={(e) => setNewQuickAction({ ...newQuickAction, actionType: e.target.value })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                  >
                    <option value="message">{t('widget.actionSendMessage')}</option>
                    <option value="link">{t('widget.actionOpenLink')}</option>
                    <option value="form">{t('widget.actionShowForm')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {newQuickAction.actionType === 'link' ? t('widget.url') : t('widget.messageText')}
                  </label>
                  <input
                    type="text"
                    value={newQuickAction.actionValue}
                    onChange={(e) => setNewQuickAction({ ...newQuickAction, actionValue: e.target.value })}
                    className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white"
                    placeholder={newQuickAction.actionType === 'link' ? t('widget.urlPlaceholder') : t('widget.messageTextPlaceholder')}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={createQuickActionMutation.isPending}
                  className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                >
                  {createQuickActionMutation.isPending ? t('widget.addingQuickAction') : t('widget.addQuickAction')}
                </button>
              </form>
            </div>

            {/* Quick Actions Preview */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4">{t('widget.previewTitle')}</h2>
              <div className="bg-[#0a0a0f] rounded-lg p-6">
                <div className="text-sm text-gray-400 mb-3">{t('widget.previewSubtitle')}</div>
                <div className="bg-[#1a1a25] rounded-lg p-4">
                  <div className="text-gray-300 text-sm mb-3">{t('widget.greetingPlaceholder')}</div>
                  <div className="flex flex-wrap gap-2">
                    {quickActions && quickActions.length > 0 ? (
                      quickActions.map((qa) => (
                        <span
                          key={qa.id}
                          className="px-3 py-1.5 bg-[#006cd4]/20 text-[#006cd4] rounded-full text-xs border border-[#006cd4]/30"
                        >
                          {qa.label}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 text-xs italic">{t('widget.noQuickActionsPreview')}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-3">{t('widget.tips')}</h2>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tipClearLabels')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tipSendMessage')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tipOpenLink')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#006cd4] mt-0.5">•</span>
                  {t('widget.tipOrder')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'leadCapture' && (
        <LeadCaptureTab />
      )}
    </div>
  )
}
